import {
  apiCommodityDeliveryCommodityDetail,
  ApiCommodityDeliveryCommodityDetailResponse,
  apiCommodityDeliveryCommodityMarketingDetail,
  ApiCommodityDeliveryCommodityMarketingDetailResponse,
  apiCommodityDeliveryCommodityStoreDetail,
  ApiCommodityDeliveryCommodityStoreDetailResponse,
  apiCommodityDeliverySkuGetSkuList,
  ApiCommodityDeliverySkuGetSkuListResponse,
  apiEvaluationEvaluateGetLabelList,
  ApiEvaluationEvaluateGetLabelListResponse,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
  apiEvaluationEvaluateList,
  apiShoppingCartList,
  ApiShoppingCartListResponse,
} from '@/api'
import { asRequiredDeep, sum } from 'vtils'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import { createModelPlus, prepareData, requiredDeep } from '@/utils'
import { defineProvider } from '@/hooks'
import { Ref } from '@vue/composition-api'

export interface RawInstantDeliveryGoodsModel
  extends RequiredDeep<ApiCommodityDeliveryCommodityDetailResponse> {
  martData: ApiCommodityDeliveryCommodityStoreDetailResponse

  cartData: RequiredDeep<ApiShoppingCartListResponse>

  isMartClosed: boolean

  surplusStock: number

  skuList: ApiCommodityDeliverySkuGetSkuListResponse

  /** 商品ID */
  goodsId: string

  /** 营销信息 */
  marketingData: ApiCommodityDeliveryCommodityMarketingDetailResponse

  /** 评论数据 */
  commentData: {
    /** 总评论 */
    total: number
    /** 好评率(99%) */
    goodRate: string
    /** 标签列表 */
    tags: RequiredDeep<ApiEvaluationEvaluateGetLabelListResponse>
    /** 最新评论列表 */
    latestComments: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']
  }

  noShare: boolean

  /** 分享参数 */
  shareParams: any

  refreshCartData: () => any

  refreshMarketing: () => any
}

export type InstantDeliveryGoodsModel = AsyncReturnType<
  typeof getInstantDeliveryGoodsModel
>

export async function getInstantDeliveryGoodsModel(goodsId: string) {
  const { goodsData } = await prepareData({
    goodsData: apiCommodityDeliveryCommodityDetail(
      {
        commodityId: goodsId,
      },
      {
        noErrorToast: true,
      },
    )
      .then(requiredDeep)
      .catch(
        () => ({} as RequiredDeep<ApiCommodityDeliveryCommodityDetailResponse>),
      ),
  })

  if (!goodsData.commodityId) {
    return createModelPlus<RawInstantDeliveryGoodsModel>({
      isOff: true,
    } as any)
  }

  const { cartData, martData, skuList, commentData } = await prepareData({
    cartData: apiShoppingCartList(
      {},
      {
        businessChannel: goodsData.merchantId,
      },
    ).then(asRequiredDeep),
    martData: apiCommodityDeliveryCommodityStoreDetail({
      storeId: goodsData.merchantId,
    }),
    skuList: apiCommodityDeliverySkuGetSkuList({
      commodityId: goodsId,
    }).then(res => res.filter(item => item.skuStatus === 1)),
    commentData: Promise.all([
      // 获取评论总数
      apiEvaluationEvaluateList({
        commodityServiceId: goodsId,
        pageIndex: 1,
        pageSize: 1,
        havingPicFlag: 0,
      }),
      // 获取最新评论、好评率
      apiEvaluationEvaluateGetLatestEvaluateList({
        commodityServiceId: goodsId,
        pageIndex: 1,
        pageSize: 1,
      }),
      // 获取评论标签
      apiEvaluationEvaluateGetLabelList({
        commodityServiceId: goodsId,
      }),
    ]).then<RawInstantDeliveryGoodsModel['commentData']>(
      ([totalSource, latestSource, tagSource]) => {
        return {
          total: totalSource.total,
          goodRate: latestSource.praiseRate as any,
          latestComments: latestSource.list as any,
          tags: tagSource as any,
        }
      },
    ),
  })

  const marketingData = await apiCommodityDeliveryCommodityMarketingDetail({
    commodityId: goodsId,
    skuId: skuList[0]?.skuId,
  })

  const InstantDeliveryGoodsModel =
    createModelPlus<RawInstantDeliveryGoodsModel>({
      ...{},
      ...goodsData,
      cartData: cartData,
      martData: martData,
      isMartClosed: martData.storeStatus !== 2,
      surplusStock: sum(skuList.map(sku => sku.surplusStock)),
      skuList: skuList,
      marketingData: marketingData,
      get commentData() {
        return commentData
      },
      get goodsId() {
        return goodsId
      },
      get noShare() {
        return !!this.supportIntegral
      },
      get shareParams() {
        return {
          title: this.commodityTitle,
          desc: this.commodityTitle,
          cover: this.coverPic,
        }
      },
      async refreshCartData() {
        this.cartData = await apiShoppingCartList(
          {},
          {
            businessChannel: this.merchantId,
          },
        ).then(asRequiredDeep)
      },
      async refreshMarketing() {
        this.marketingData = await apiCommodityDeliveryCommodityMarketingDetail(
          {
            commodityId: goodsId,
            skuId: skuList[0]?.skuId,
          },
        )
      },
    })

  return InstantDeliveryGoodsModel
}

export const InstantDeliveryGoodsModelProvider =
  defineProvider<Ref<InstantDeliveryGoodsModel>>()
